import {createAction} from 'typesafe-actions';

import type {
  BaseSearchHistoryItem,
  PanelBankSettings,
  PanelPlacementOverride,
} from '../../../components/PanelBank/types';
import {type LinePlotSettings} from '../../../components/WorkspaceDrawer/Settings/types';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import type * as WorkspaceSettingsTypes from '../workspaceSettings/types';
import type * as Types from './types';

export const noOp = createAction(
  '@view/panelBankConfig/noOp',
  action => (ref: Types.Ref) => action({ref})
);

export const updateSettings = createAction(
  '@view/panelBankConfig/updateSettings',
  action => (ref: Types.Ref, panelBankSettings: Partial<PanelBankSettings>) =>
    action({ref, panelBankSettings})
);

export const pinSection = createAction(
  '@view/panelBankConfig/pinSection',
  action => (ref: Types.Ref, sectionRef: PanelBankSectionConfigTypes.Ref) =>
    action({ref, sectionRef})
);

export const updatePanelSearchHistory = createAction(
  '@view/panelBankConfig/updateSearchHistory',
  action =>
    (ref: Types.Ref, searchHistory: BaseSearchHistoryItem[] | undefined) =>
      action({ref, searchHistory})
);

export const setCurrentPanelSearch = createAction(
  '@view/panelBankConfig/setCurrentPanelSearch',
  action => (ref: Types.Ref, searchQuery: string) => action({ref, searchQuery})
);

// Add a new section
// By default, inserts new section before the given sectionRef.
// Pass addAfter=true if you want to insert after the given section.
export const addSection = createAction(
  '@view/panelBankConfig/addSection',
  action =>
    (
      ref: Types.Ref,
      sectionRef: PanelBankSectionConfigTypes.Ref,
      options?: {
        addAfter?: boolean;
        newSectionName?: string;
        panelRefs?: PanelTypes.Ref[];
      }
    ) =>
      action({ref, sectionRef, options})
);

export const deleteSection = createAction(
  '@view/panelBankConfig/deleteSection',
  action =>
    (
      ref: Types.Ref,
      sectionRef: PanelBankSectionConfigTypes.Ref,
      workspaceSettingsRef?: WorkspaceSettingsTypes.Ref
    ) =>
      action({ref, sectionRef, workspaceSettingsRef})
);

/**
 * This actions handles moving the active section to the target section's position in the array.
 * This reorders the sectionRefs in the panel bank config.
 *
 * @param ref This is the panel bank config ref
 * @param activeSectionRefId This is the section that is being moved
 * @param targetSectionRefId This is the section that indicates what position the active section will be moved to
 */
export const moveSection = createAction(
  '@view/panelBankConfig/moveSection',
  action =>
    (ref: Types.Ref, activeSectionRefId: string, targetSectionRefId: string) =>
      action({ref, activeSectionRefId, targetSectionRefId})
);

/**
 * This actions handles IMMUTABLY moving the active panel to the target section's position in the array.
 * This reorders the panelRefs in the panelBankSectionConfig. This assumes both active and target sections already exist
 * This handles both moving a panel within the same section and moving a panel to a different section.
 *
 * NOTE: there is still an IMMER version of this action that is used for the `movePanelToNewSection` action.
 *
 * @param ref This is the panel bank config ref
 * @param active This is the panel that is being moved
 * @param target This is the info that indicates what position the active panel will be moved to and which section
 */
export const movePanel = createAction(
  '@view/panelBankConfig/movePanel',
  action =>
    (
      ref: Types.Ref,
      active: {
        panelRef: PanelTypes.Ref;
        sectionRef: PanelBankSectionConfigTypes.Ref;
      },
      target: {
        panelRef: PanelTypes.Ref | null;
        sectionRef: PanelBankSectionConfigTypes.Ref;
        insertDir: 'before' | 'after';
      }
    ) =>
      action({ref, active, target})
);

/**
 * This is the corresponding immutable undo action for movePanel.
 */
export const undoMovePanel = createAction(
  '@view/panelBankConfig/undoMovePanel',
  action =>
    (
      ref: Types.Ref,
      active: {
        panelRef: PanelTypes.Ref;
        prevIsPanelAuto: boolean | undefined;
        sectionRef: PanelBankSectionConfigTypes.Ref;
        prevSectionPanelsAuto: boolean | undefined;
        /** Panel key for sparse override restoration (optional) */
        panelKey?: string;
        /** Previous override to restore on undo (undefined means remove override) */
        prevOverride?: PanelPlacementOverride;
      },
      target: {
        panelRef: PanelTypes.Ref | null; // a section can be empty, so the panelRef can be null
        sectionRef: PanelBankSectionConfigTypes.Ref;
        insertDir: 'before' | 'after'; // This is the direction the panel will be inserted in the target section
        prevSectionPanelsAuto: boolean | undefined;
      }
    ) =>
      action({ref, active, target})
);

/**
 * This is the immer version of movePanel. Parts of the movePanelInternal was moved to the non-immutable version (above).
 * This is still used for immer `movePanelToNewSection` action that creates a new section before moving the panel.
 */
export const movePanelImmer = createAction(
  '@view/panelBankConfig/movePanelImmer',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref, // the panel we're deleting
      fromSectionRef: PanelBankSectionConfigTypes.Ref, // the panelbank section we're moving the panel from
      toSectionRef: PanelBankSectionConfigTypes.Ref, // the panelbank section we're moving the panel to
      toIndex?: number, // the index we're moving the panel to in toSectionRefs
      inactivePanelRefIDs?: Set<string>
    ) =>
      action({
        ref,
        panelRef,
        fromSectionRef,
        toSectionRef,
        toIndex,
        inactivePanelRefIDs,
      })
);

export const movePanelToNewSection = createAction(
  '@view/panelBankConfig/movePanelToNewSection',
  action =>
    (
      ref: Types.Ref,
      args: {
        panelRef: PanelTypes.Ref; // the panel we're deleting
        fromSectionRef: PanelBankSectionConfigTypes.Ref; // the panelbank section we're moving the panel from
        newSectionName: string;
      }
    ) =>
      action({ref, args}) // panelRef, fromSectionRef, toSectionRef, toIndex})
);

export const updateSettingsAndSortPanels = createAction(
  '@view/panelBankConfig/updateSettingsAndSortPanels',
  action =>
    (
      ref: Types.Ref,
      args: {
        workspaceSettingsRef: WorkspaceSettingsTypes.Ref;
        sortAlphabetically: boolean | undefined;
      }
    ) =>
      action({ref, args})
);

export const openOrCloseAllSections = createAction(
  '@view/panelBankConfig/openOrCloseAllSections',
  action => (ref: Types.Ref, isOpen: boolean) => action({ref, isOpen})
);

export const clearAllPanels = createAction(
  '@view/panelBankConfig/clearAllPanels',
  action =>
    (ref: Types.Ref, workspaceSettingsRef: WorkspaceSettingsTypes.Ref) =>
      action({ref, workspaceSettingsRef})
);

export const clearAllPanelsUndo = createAction(
  '@view/clearAllPanelsUndo',
  action =>
    (
      ref: Types.Ref,
      workspaceSettingsRef: WorkspaceSettingsTypes.Ref,
      sectionRefs: PanelBankSectionConfigTypes.Ref[]
    ) =>
      action({
        ref,
        workspaceSettingsRef,
        sectionRefs,
      })
);

export const updateAllLinePlotSectionSettings = createAction(
  '@view/panelBankConfig/updateAllLinePlotSectionSettings',
  action => (ref: Types.Ref, settings: Partial<LinePlotSettings>) =>
    action({ref, settings})
);

export const addPanelsBySpec = createAction(
  '@view/panelBankConfig/addPanelsBySpec',
  action =>
    (ref: Types.Ref, specs: Types.KeySpecList, shouldAlphaInsert?: boolean) =>
      action({ref, specs, shouldAlphaInsert})
);

export const addPanelsBySpecUndo = createAction(
  '@view/panelBankConfig/addPanelsBySpecUndo',
  action =>
    (
      ref: Types.Ref,
      /** Restore panelBankConfig.sectionRefs to this list of sections */
      sectionsMetadata: Types.UndoAddPanelsBySpecSectionMetadata[]
    ) =>
      action({ref, sectionsMetadata})
);
